import { DefaultButton, Pivot, PivotItem, PrimaryButton, Spinner } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Prompt, useParams } from 'react-router-dom';
import { bool, object, string } from 'yup';
import ErrorComponent from '../../../../Util/ErrorComponent';
import SuccessComponent from '../../../../Util/SuccessComponent';
import ConfirmModal from '../../HubSettings/ConfirmModal';
import SelectCobrandsForm from '../../HubSettings/Forms/SelectCobrandsForm';
import ShowCobrandList from '../../HubSettings/ShowCobrandList';
import AdvisorsForm from '../Components/AdvisorsForm';
import FeatureSummary from '../../../../Util/FeatureSummary';

const coBrandSettingsSchema = object({
    title: string().required(),
    bannerDescription: string().required(),
    internationalOn: bool().required(),
    onOffDefault: bool().required(),
});

const newCoBrandSettingsObject = {
    client: '',
    cobrandTag: '',
    cobrand: '',
    title: '',
    bannerDescription: '',
    internationalOn: 0,
    onOffDefault: 0,
};

const WizardPage = () => {
    const { coBrandID: coBrandIDFromParams } = useParams();
    const [selectedKey, setSelectedKey] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [totalToUpdate, setTotalToUpdate] = useState(0);
    const [{ data: coBrandData, loading: cobrandLoading }, getCobrandAdvisorSettings] = useAxios(
        { url: `internal/cobrand/${coBrandIDFromParams}/advisor-settings`, method: 'GET' },
        { manual: true },
    );
    const [{ data, loading: submitting }, submitForm] = useAxios(
        { url: `internal/cobrand/advisor-settings` },
        { manual: true },
    );

    const [{ data: cobrandIdData, loading: fetchingCobrandIds }, getCobrandIds] = useAxios(
        { url: `internal/cobrand/ids-from-tags`, method: 'POST' },
        { manual: true },
    );

    useEffect(() => {
        if (coBrandIDFromParams && coBrandIDFromParams !== 'bulk') {
            getCobrandAdvisorSettings();
        }
    }, [coBrandIDFromParams]);

    const handleLinkClick = (item) => {
        const itemKey = item.props.itemKey;
        setSelectedKey(Number(itemKey));
    };
    const handlePrevClick = () => setSelectedKey(Number(selectedKey) - 1);
    const handleNextClick = () => setSelectedKey(Number(selectedKey) + 1);

    if (cobrandLoading) {
        return <Spinner className="absolute-center" text="Loading Cobrand Hub Settings..." />;
    }

    return (
        <Formik
            initialValues={
                coBrandData
                    ? {
                          ...coBrandData,
                          internationalOn: coBrandData.internationalOn === true ? 1 : 0,
                          onOffDefault: coBrandData.onOffDefault === true ? 1 : 0,
                      }
                    : newCoBrandSettingsObject
            }
            validationSchema={coBrandSettingsSchema}
            onSubmit={async (values, { resetForm }) => {
                let result;
                setIsConfirmOpen(false);

                if (coBrandIDFromParams) {
                    result = await submitForm({
                        url: 'internal/cobrand/advisor-settings',
                        method: 'PUT',
                        data: values,
                    });
                } else {
                    const getCobrandIdsData = {
                        client: values.client || null,
                        cobrand: values.cobrand || null,
                        cobrandTag: values.cobrandTag || null,
                    };
                    const CobrandIds = await getCobrandIds({
                        data: getCobrandIdsData,
                    });
                    if (CobrandIds.data.length === 0) return alert('Select cobrand(s) before saving');
                    const idListToUpdate = await CobrandIds.data.reduce((arr, current) => {
                        return [...arr, current.coBrandId];
                    }, []);

                    result = await submitForm({
                        url: 'internal/cobrand/advisor-settings',
                        method: 'POST',
                        data: { ...values, coBrandIDs: idListToUpdate },
                    });
                }
                resetForm({ values });

                if (!result.isAxiosError) {
                    setShowSuccess(true);
                } else {
                    setShowError(true);
                }
            }}
            validateOnBlur
            isInitialValid={false}
        >
            {({ dirty, isValid, values, handleSubmit }) => {
                console.log({ dirty, isValid, values });
                return (
                    <Form style={{ maxWidth: '800px' }}>
                        <Prompt when={dirty} message="You have unsaved changes, are you sure you want to leave?" />

                        <div style={{ width: '100%' }} className="mt-1">
                            {!coBrandIDFromParams ? (
                                <Pivot selectedKey={String(selectedKey)} onLinkClick={handleLinkClick}>
                                    <PivotItem headerText="Select Cobrand" itemKey={'0'}>
                                        <SelectCobrandsForm />
                                    </PivotItem>
                                    <PivotItem headerText="Advisor Settings" itemKey={'1'}>
                                        <AdvisorsForm />
                                    </PivotItem>
                                </Pivot>
                            ) : (
                                <AdvisorsForm />
                            )}
                            <hr />
                            {!coBrandIDFromParams && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }} className="mt-1">
                                    {selectedKey === 0 ? (
                                        <div />
                                    ) : (
                                        <DefaultButton text="Prev" onClick={handlePrevClick} />
                                    )}
                                    {selectedKey === 1 ? (
                                        <div />
                                    ) : (
                                        <PrimaryButton text="Next" onClick={handleNextClick} />
                                    )}
                                </div>
                            )}
                            <div style={{ display: 'flex' }} className="mt-1">
                                <PrimaryButton
                                    onClick={() => (coBrandIDFromParams ? handleSubmit() : setIsConfirmOpen(true))}
                                    text="Save"
                                    disabled={!isValid || !dirty}
                                />
                            </div>
                        </div>

                        {selectedKey === 0 && (
                            <ShowCobrandList
                                clients={values.client}
                                tags={values.cobrandTag}
                                cobrands={values.cobrand}
                                setTotalToUpdate={setTotalToUpdate}
                            />
                        )}

                        <ConfirmModal
                            isConfirmOpen={isConfirmOpen}
                            closeConfirmModal={() => setIsConfirmOpen(false)}
                            handleSubmit={handleSubmit}
                            totalToUpdate={totalToUpdate}
                        />

                        <SuccessComponent
                            successMessage="Advisor Settings Updated."
                            showSuccess={showSuccess}
                            setShowSuccess={setShowSuccess}
                        />
                        <ErrorComponent
                            errorMessage="Errors in form, check all tabs for highlighted fields."
                            showError={showError}
                            setShowError={setShowError}
                        />
                        <FeatureSummary
                            label="About Dashboard Advisors"
                            summary="Configure advisor/counselor settings for cobrands. Set the title, banner description, international targeting, and default on/off status. Use bulk update mode to apply settings across multiple cobrands by selecting clients or cobrand tags."
                        />
                        <div></div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
